import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { 
  Box,
  Card,
  Grid,
  Hidden,
  Avatar, 
  makeStyles, 
  Typography,
} from '@material-ui/core';

import { AvatarWithoutPhoto, Copyright } from '../Shared';
import { SkeletonSidebarProfile } from '../Skeletons';
import { ImagineLinkify } from '../TextLinkify';

const useStyle = makeStyles((theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  card: {
    width: '100%',
    borderRadius: 8,
    border: '1px solid',
    borderColor: theme.palette.grey.main,
    padding: theme.spacing(3),
    '&:first-child': {
      marginBottom: theme.spacing(2)
    }
  },
  avatar: {
    display: 'flex',
    justifyContent: 'center',
  },
  large: {
    width: theme.spacing(16),
    height: theme.spacing(16),
    [theme.breakpoints.down('sm')]: {
      width: theme.spacing(14),
      height: theme.spacing(14),
    },
  },
  name: {
    fontSize: 20,
    fontWeight: 600,
    textAlign: 'center',
    marginTop: theme.spacing(2),
    color: theme.palette.neutrals.low.dark,
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1.5),
    },
  },
  position: {
    fontSize: 14,
    fontWeight: 600,
    textAlign: 'center',
    marginTop: theme.spacing(1),
    color: theme.palette.neutrals.low.light,
    [theme.breakpoints.down('sm')]: {
      marginTop: 0
    },
  },
  email: {
    fontSize: 12,
    fontWeight: 600,
    textAlign: 'center',
    margin: theme.spacing(2, 0),
    color: theme.palette.neutrals.low.dark,
  },
  about: {
    fontSize: 14,
    marginBottom: theme.spacing(2),
    color: theme.palette.neutrals.low.medium,
  },
  titleAbout: {
    fontSize: 14,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(2),
    color: theme.palette.neutrals.low.dark,
  },
}));

export function SidebarProfile({ user }) {
  const classes = useStyle();
  const { t } = useTranslation();
  const loading = useSelector((state) => state.profile.loading);

  return (
    <div className={classes.container}>
      {loading ? (
        <Card elevation={0} className={classes.card}>
          <SkeletonSidebarProfile />
        </Card>
        
      ) : (
        <Grid container>
          <Card elevation={0} className={classes.card}>
            <Grid item xs={12} className={classes.avatar}>
              {user.attachment?.url ? (
                <Avatar src={user.attachment?.url} className={classes.large} />
              ) : (
                <AvatarWithoutPhoto superLarge iconStyle={{ fontSize: 80 }}/>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h5" component="h1" className={classes.name}>
                {user.name}
              </Typography>

              <Typography variant="subtitle1" component="h3" className={classes.position}>
                {user.position}
                {user.businessUnitLocal?.occupationArea?.name && (
                  <Box fontWeight="fontWeightMedium" fontSize={14}>
                    em {user.businessUnitLocal?.occupationArea?.name}
                  </Box>
                )}
              </Typography>

              {user.email && user.email.includes('@') && 
                <Typography variant="body1" className={classes.email}>
                  {user.email}
                </Typography>
              }
            </Grid>
          </Card>
          
          <Card elevation={0} className={classes.card}>
            <Grid item xs={12}>
              <Typography variant="body1" className={classes.titleAbout}>
                {t('sobre_mim')}
              </Typography>
              {user.about && 
                <Typography variant="body1" className={classes.about}>
                  <ImagineLinkify>
                    {user.about}
                  </ImagineLinkify>
                </Typography>
              }
            </Grid>
          </Card>
        </Grid>
      )}
      <Hidden smDown>
        <Copyright />
      </Hidden>   
    </div>
  );
}
